import axios from 'axios';
import config from '../config/api';

// Este servicio usa '/api' para varios catálogos (tiposocias, socias, etc.)
// y '/api/reportes' específicamente para endpoints de reportes.
const API_URL = config.endpoints.reportes; // normalmente '/api'
const REPORTES_URL = `${config.API_BASE_URL}/reportes`; // '/api/reportes'

export const getTiposSocias = async () => {
  try {
    const response = await axios.get(`${API_URL}/tiposocias`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener tipos de socias:', error);
    throw error;
  }
};

export const getSocias = async () => {
  try {
    const response = await axios.get(`${API_URL}/socias`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener socias:', error);
    throw error;
  }
};

// Tratamientos y medicamentos por socia
export const getTratamientosPorSocia = async (sociaId) => {
  if (!sociaId) return [];
  try {
    const response = await axios.get(`${API_URL}/tratamiento/socia/${sociaId}`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener tratamientos por socia:', error);
    throw error;
  }
};

// Reporte de socias por provincia (JSON)
export const getSociasPorProvincia = async () => {
  try {
    const response = await axios.get(`${REPORTES_URL}/socias-provincia?format=json`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener reporte de socias por provincia:', error);
    throw error;
  }
};

// Descargar PDF de socias por provincia
export const descargarReporteSociasPorProvincia = async () => {
  try {
    const response = await axios.get(`${REPORTES_URL}/socias-provincia`, {
      responseType: 'blob',
    });
    // Descargar el archivo PDF
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'reporte_socias_provincia.pdf');
    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch (error) {
    console.error('Error al descargar PDF de socias por provincia:', error);
    throw error;
  }
};

// Reporte de socias por ciudad/cantón (JSON)
export const getSociasPorCanton = async () => {
  try {
    const response = await axios.get(`${REPORTES_URL}/socias-canton?format=json`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener reporte de socias por ciudad:', error);
    throw error;
  }
};

// Descargar PDF de socias por ciudad/cantón
export const descargarReporteSociasPorCanton = async () => {
  try {
    const response = await axios.get(`${REPORTES_URL}/socias-canton`, {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'reporte_socias_ciudad.pdf');
    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch (error) {
    console.error('Error al descargar PDF de socias por ciudad:', error);
    throw error;
  }
};

// Incidencias de ubicación (JSON)
export const getIncidenciasUbicacion = async ({ nivel = 'pais' } = {}) => {
  try {
    const params = new URLSearchParams();
    if (nivel) params.append('nivel', nivel);
    const url = `${REPORTES_URL}/incidencias-ubicacion?${params.toString()}`;
    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    console.error('Error al obtener incidencias de ubicación:', error);
    throw error;
  }
};

// Reporte de socias con discapacidad (JSON)
export const getSociasConDiscapacidad = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    queryParams.append('format', 'json');
    if (params.carnet) queryParams.append('carnet', params.carnet);
    if (params.tipo) queryParams.append('tipo', params.tipo);
    if (params.grado) queryParams.append('grado', params.grado);
    if (params.rango) queryParams.append('rango', params.rango);
    if (params.pctMin) queryParams.append('pctMin', params.pctMin);
    if (params.pctMax) queryParams.append('pctMax', params.pctMax);
    
    const url = `${REPORTES_URL}/discapacidad?${queryParams.toString()}`;
    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    console.error('Error al obtener reporte de socias con discapacidad:', error);
    throw error;
  }
};

// Descargar PDF de socias con discapacidad
export const descargarReporteDiscapacidad = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    if (params.carnet) queryParams.append('carnet', params.carnet);
    if (params.tipo) queryParams.append('tipo', params.tipo);
    if (params.grado) queryParams.append('grado', params.grado);
    if (params.rango) queryParams.append('rango', params.rango);
    if (params.pctMin) queryParams.append('pctMin', params.pctMin);
    if (params.pctMax) queryParams.append('pctMax', params.pctMax);
    
    const url = `${REPORTES_URL}/discapacidad?${queryParams.toString()}`;
    const response = await axios.get(url, { responseType: 'blob' });
    
    const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = blobUrl;
    link.setAttribute('download', 'reporte_discapacidad.pdf');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error('Error al descargar PDF de socias con discapacidad:', error);
    throw error;
  }
};

// Reporte de socias afiliadas al IESS (JSON)
export const getSociasAfiliadasIESS = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    queryParams.append('format', 'json');
    if (params.afiliado) queryParams.append('afiliado', params.afiliado);
    
    const url = `${REPORTES_URL}/iess?${queryParams.toString()}`;
    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    console.error('Error al obtener reporte de socias afiliadas al IESS:', error);
    throw error;
  }
};

// Descargar PDF de socias afiliadas al IESS
export const descargarReporteIESS = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    if (params.afiliado) queryParams.append('afiliado', params.afiliado);
    
    const url = `${REPORTES_URL}/iess?${queryParams.toString()}`;
    const response = await axios.get(url, { responseType: 'blob' });
    
    const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = blobUrl;
    link.setAttribute('download', 'reporte_iess.pdf');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error('Error al descargar PDF de afiliadas al IESS:', error);
    throw error;
  }
};

// Reporte de talla por socia (JSON)
export const getTallaPorSocia = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    if (params.sociaId) queryParams.append('sociaId', params.sociaId);
    if (params.fechaInicio) queryParams.append('fechaInicio', params.fechaInicio);
    if (params.fechaFin) queryParams.append('fechaFin', params.fechaFin);
    
    const url = `${REPORTES_URL}/talla${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    console.error('Error al obtener datos de talla por socia:', error);
    throw error;
  }
};

// Reporte de edad de detección por país (JSON)
export const getEdadDeteccion = async () => {
  try {
    const response = await axios.get(`${REPORTES_URL}/edad-deteccion`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener datos de edad de detección:', error);
    throw error;
  }
};